// The notices column and the timer line (R430, R824), as one small module
// that mounts into a given element.
//
// Both were private members of `Overlay` until the theme preview needed them
// (R806). The preview shows the notices of a theme inside its own frame, so
// it must run the same code the game runs: a copy of the markup would be a
// second answer that drifts. `Overlay` now builds one `NoticeColumn` and two
// `TimerLine`s, and the preview does the same on the elements of its frame.
//
// **The six looks.** `notices` is a field of the World body (themes.md
// §3.4), and a value of it changes where the timer stands, where the lines
// stand, how deep the column is and how long a line lives. One entry of
// `NOTICE_RENDERERS` holds what TypeScript must know about a value; the
// stylesheet holds the rest, under `#ui.theme-notices-<id>`. The mockup card
// of round twenty-four is the spec of each one (ui-map-interface.md §8).
//
// Three rules hold under every value, and §15 states them: the claim order
// of the timer line, the two tones, and the toast rules — the life, the
// repeat restart and the text written as text and never as markup.

import {
  ALERT_WEIGHTS, ALARM_NOTICE_LINES, CHROME_LEDGER_LINES, CHROME_LEDGER_NOTICE_MS, CHROME_NOTICE_DEDUP_MS, CHROME_NOTICE_MIN_MS,
  CHROME_NOTICE_CHAR_MS, CHROME_NOTICE_MS, CHROME_NOTICE_QUEUE_MAX,
} from '../gen/presentation';
import { notices as SN } from '../gen/strings';
import { AUTO_CHROME_LOOK } from './chromelook';
import type { NoticeStyle } from './settings';
import { clamp01 } from '../render/math';
import { isPrimaryPress } from './pressroute';

/** What a caller may say about one notice. */
export interface NoticeOptions {
  error?: boolean;
  ms?: number;
  key?: string;
  /** A kept line (R2059): a newer line drops an older line that is not
   *  kept first, so the line stands for its whole life. The hub keeps the
   *  surrender notice of an arcade match (`Notice.keep`). */
  keep?: boolean;
  /** The place of the event (R2370): the cell of its emission, 0 or
   *  absent for none (`Notice.cell`). A line with a place is a GO TO press
   *  when the column has a handler (`NoticeColumn.setGo`). */
  cell?: number;
}

/**
 * The class of the alert weight of an event (round 088 card 17, R1753,
 * R2637): ` w1` for a line with no GO TO word, ` w2` for a GO TO in the
 * dark face, '' for an event `ALERT_WEIGHTS` leaves out. The key of a
 * server notice is its event id.
 */
export function weightClass(key: string): string {
  const w = ALERT_WEIGHTS[key];
  return w ? ` w${w}` : '';
}

/** How many lines the alternative looks keep by default. The oldest goes
 *  first. The AUTO look keeps `ALARM_NOTICE_LINES` (R1753). */
const MAX_LINES = 5;

/**
 * One claim on the timer line: everything a style may want to draw.
 *
 * `Overlay` picks ONE claim per frame in the order §8 states — link down >
 * seaquake > founding truce — and the helpers of `ui/format.ts` build the
 * parts from `data/locale/en/ui.toml`. The parts are separate because four
 * of the six styles set the name of the clock apart from its figure;
 * `sentence` is the whole composed tab the AUTO style writes, so that value
 * keeps the words it has always had.
 */
export interface TimerClaim {
  /** True for a fault (red), false for a schedule (gold). §8 states which. */
  fault: boolean;
  /** The mark of the clock, for a style with room for a mark. */
  glyph: string;
  /** The long name of the clock. */
  label: string;
  /** The short name, for a style with one lane and no room. */
  short: string;
  /** The figure that counts down. */
  figure: string;
  /** The reason alone, without the figure. */
  tail: string;
  /** The whole tab the AUTO style writes. It may carry markup. */
  sentence: string;
}

/**
 * What one notices style tells TypeScript.
 *
 * Everything else a value changes is CSS, under `#ui.theme-notices-<id>`.
 */
export interface NoticeRenderer {
  /**
   * Where the two timer elements stand.
   *
   * `topbar` keeps them children of `#topbar`, where R430 put them: they
   * travel home with the bar and they displace no panel. `column` moves
   * them into a cell of `#notices`, because the value draws one lane that
   * carries the timer and the latest line together.
   */
  timerHost: 'topbar' | 'column';
  /**
   * The markup of the tab for one claim.
   *
   * `tab` is the element the answer is written to. A style that must set a
   * property on the tab itself sets it here; `bare-type` is the one that
   * does, because its hairline takes the tone of the claim.
   */
  timer(tab: HTMLElement, claim: TimerClaim): string;
  /** Fills one line. The text goes in as TEXT and never as markup (§15). */
  toast(node: HTMLElement, text: string, opts: NoticeOptions, stamp: string): void;
  /** How many lines the column keeps. */
  lines: number;
  /** The life of a line, where the style overrides the event table. */
  life?: number;
}

/** One child of a line, with its text already in it. */
function cell(tag: string, cls: string, text = ''): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
}

/** The mark a line leads with, in the two styles that lead with one. */
function lineGlyph(opts: NoticeOptions): string {
  return opts.error ? SN.glyph_error : SN.glyph_note;
}

/** A line that is its text alone. The AUTO look draws its tone mark in
 *  the stylesheet (a ring or a diamond, R1819), so its text needs none. */
function textToast(node: HTMLElement, text: string): void {
  node.textContent = text;
}

/**
 * The text of a line under a look that draws no mark of its own shape:
 * an error leads with the error mark, so the tone never rests on colour
 * alone (R3100, ui-map-interface.md §15).
 */
function markedText(text: string, opts: NoticeOptions): string {
  return opts.error ? `${SN.glyph_error} ${text}` : text;
}

/**
 * The six notices looks (themes.md §3.4, R824). Position 0 of
 * `notice_styles` is `drain`, and `drain` is what ships.
 */
export const NOTICE_RENDERERS: Record<NoticeStyle, NoticeRenderer> = {
  // Card 02 "Drain Line" (R430): the tab under the bar's right end carries
  // the whole composed sentence, and a line is one bordered plate. The
  // alarm screen (R1753, card 16 of round 082) keeps two lines at most at
  // the right under the readout line, and the stylesheet fades the older.
  drain: {
    timerHost: 'topbar',
    timer: (_tab, c) => c.sentence,
    toast: textToast,
    lines: ALARM_NOTICE_LINES,
  },
  // Card 03 "Ledger": news is a log, so it is written as one — top left,
  // each line with the world-clock stamp it arrived at and a coloured rule.
  // A ledger is off the bar's centre line, so it holds more and longer.
  ledger: {
    timerHost: 'topbar',
    timer: (_tab, c) =>
      `<small>${c.label}</small><b>${c.figure}</b><span class="tail">${c.tail}</span>`,
    toast: (node, text, opts, stamp) => {
      node.append(cell('span', 'ts', stamp), cell('span', 'rule'), cell('span', '', markedText(text, opts)));
    },
    lines: CHROME_LEDGER_LINES,
    life: CHROME_LEDGER_NOTICE_MS,
  },
  // Card 04 "Split Lane": one strip under the bar with two cells, the timer
  // at its left and the latest line at its right. The older lines are a
  // count and not a stack, and the strip is gone when it holds neither.
  'split-lane': {
    timerHost: 'column',
    timer: (_tab, c) => `${c.glyph} ${c.short} <b>${c.figure}</b>`,
    toast: (node, text, opts) => { node.textContent = markedText(text, opts); },
    lines: 1,
  },
  // Card 05 "Under the Clock": a countdown belongs with the clock it counts
  // on, so the tab hangs under the bar's right end in the bar's own glass.
  // Each line is a card with a mark column and a hairline that drains.
  'under-clock': {
    timerHost: 'topbar',
    timer: (_tab, c) => `${c.glyph} <b>${c.figure}</b><span class="d">${c.short}</span>`,
    toast: (node, text, opts) => {
      node.append(cell('span', 'g', lineGlyph(opts)), cell('span', '', text), cell('i', 'life'));
    },
    lines: MAX_LINES,
  },
  // Card 08 "Ribbon": a full-width strip under the bar. The latest line
  // types in at the left, the clock stands at the right, and the strip
  // slides away when it holds nothing. One line at a time, so a burst of
  // news reads as a ticker; the rest is a count that names the inbox.
  ribbon: {
    timerHost: 'column',
    timer: (_tab, c) =>
      `${c.glyph} <span class="lg">${c.label}</span><span class="sm">${c.short}</span>`
      + ` <b>${c.figure}</b><span class="d">${c.tail}</span>`,
    toast: (node, text, opts) => {
      node.append(cell('span', 'g', lineGlyph(opts)), cell('span', '', text));
    },
    lines: 1,
  },
  // Card 10 "Bare Type": nothing is boxed, so the lane never reads as a
  // panel a player could press. The timer is one letterspaced line with a
  // hairline in the tone of the claim, and a line is text with a dot.
  'bare-type': {
    timerHost: 'topbar',
    timer: (tab, c) => {
      // The one style that writes through `tab`: the hairline is an
      // `::after` of the tab, and it takes the tone from this property.
      tab.style.setProperty('--tone', c.fault ? 'var(--bad)' : 'var(--hero-ink)');
      return `${c.label} <span class="d">· ${c.tail}</span> <b>${c.figure}</b>`;
    },
    toast: (node, text, opts) => {
      node.append(cell('span', 'dot'), cell('span', '', markedText(text, opts)));
    },
    lines: MAX_LINES,
  },
};

/**
 * One timer line: the element, its tab, and the three things a frame writes.
 *
 * It owns what `Overlay.renderTruce` and `Overlay.setLink` wrote by hand
 * before R824 — the `on` and `bad` classes, the tab's markup and `--frac` —
 * so the two callers state a claim and a share, and never a class.
 *
 * The markup is written only when it changed (§10.3). `--frac` carries its
 * own change guard in `writeDrainFrac`.
 */
export class TimerLine {
  private styleId: NoticeStyle;
  /** The markup on screen, or null before the first write and after a
   *  style change, which must rewrite whatever it wrote before. */
  private lastHtml: string | null = null;
  /** The tone on screen. It is its own guard, because two claims can read
   *  the same and differ in tone — a look that writes the figure alone
   *  writes the same tab for a schedule and for a fault. */
  private lastFault: boolean | null = null;
  private lastFrac = -1;

  constructor(
    readonly el: HTMLElement,
    readonly tab: HTMLElement,
    style: NoticeStyle = AUTO_CHROME_LOOK.notices,
  ) {
    this.styleId = style;
  }

  /** The look in force. */
  get style(): NoticeStyle { return this.styleId; }

  /** Sets the look. The game writes it one time, the preview on every show. */
  setStyle(id: NoticeStyle): void {
    if (id === this.styleId) return;
    this.styleId = id;
    this.lastHtml = null;
    this.lastFault = null;
  }

  /**
   * States one claim, or no claim at all, and how much of the schedule is
   * still to run. A line with no claim draws nothing.
   */
  set(claim: TimerClaim | null, frac: number): void {
    const html = claim ? NOTICE_RENDERERS[this.styleId].timer(this.tab, claim) : '';
    if (html !== this.lastHtml) {
      this.lastHtml = html;
      this.tab.innerHTML = html;
      this.el.classList.toggle('on', html !== '');
    }
    // Gold is a schedule and red is a fault (§8). The stylesheet paints the
    // line, the fill and the tab from this one class.
    const fault = claim !== null && claim.fault;
    if (fault !== this.lastFault) {
      this.lastFault = fault;
      this.el.classList.toggle('bad', fault);
    }
    this.lastFrac = writeDrainFrac(this.el, this.lastFrac, frac);
  }
}

/**
 * One notices column: the lines, the repeat rule and the two toast clocks.
 *
 * The two clocks are the SERVER's: eventcfg states them and the hello
 * catalog ships them, so the owner of the column sets both at the hello.
 * The values below cover a line raised before the first hello, and they
 * ride presentation.toml (R312).
 *
 * Under a `column` style the element is a lane and not a stack: the column
 * builds four cells inside it — the timer cell, a divider, the line cell
 * and the count — and the caller stands the two timer elements in the
 * first one.
 */
export class NoticeColumn {
  /** How long a line with no duration of its own stands. */
  private ms = CHROME_NOTICE_MS;
  /** How close together two identical lines must be to count as a repeat. */
  private dedupMs = CHROME_NOTICE_DEDUP_MS;
  /** The line on screen now, for the repeat test. */
  private last: {
    node: HTMLElement; key: string; text: string; at: number;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;

  /** The expiry timer of each line, so the overflow can stop the timer of
   *  a line it drops (R1353). A weak map holds no line after it has gone. */
  private timers = new WeakMap<Element, ReturnType<typeof setTimeout>>();
  /** What each line said, so `liftStanding` can say it again later. */
  private said = new WeakMap<Element, { text: string; opts: NoticeOptions }>();
  /** When each line came on screen, for the least life of the cap (R3081). */
  private shownAt = new WeakMap<Element, number>();
  /** The lines past the cap, oldest first (R3081). Each one waits until a
   *  line goes, or until a line that is not kept has stood
   *  `CHROME_NOTICE_MIN_MS`. */
  private queue: { text: string; opts: NoticeOptions; key: string }[] = [];
  /** The timer of the next queue step, or 0. */
  private pumpTimer: ReturnType<typeof setTimeout> | 0 = 0;
  /** The place of each line that is a GO TO press (R2370). */
  private place = new WeakMap<Element, number>();
  /** What a press on a line with a place does, or null: the theme preview
   *  sets none, so its lines stay inert. */
  private go: ((cell: number) => void) | null = null;

  /** Which of the notice looks the column draws (themes.md §3.4). */
  private styleId: NoticeStyle;
  /** The four cells of a `column` style, or null under the other four. */
  private cells: {
    tm: HTMLElement; div: HTMLElement; nt: HTMLElement; more: HTMLElement;
  } | null = null;
  /**
   * The world clock the top bar shows, as a stamp. `ledger` writes it
   * against each line. The column is given the reading rather than the
   * state, because it holds no game state of its own.
   */
  private clock: () => string = () => '';
  /** Whether a timer claims the lane now. `sync` is told. */
  private timerOn = false;
  private lastMore = '';
  private lastEmpty: boolean | null = null;

  constructor(readonly el: HTMLElement, style: NoticeStyle = AUTO_CHROME_LOOK.notices) {
    this.styleId = style;
    this.build();
    // One listener for every line (R2370). The column takes no pointer; a
    // line with a place takes it alone, and the press bubbles up to here.
    // The lane is fixed chrome, so the line acts on the press (§16.8).
    el.addEventListener('pointerdown', (ev) => this.press(ev));
  }

  /** The look in force. */
  get style(): NoticeStyle { return this.styleId; }

  /**
   * Gives the column what a press on a line with a place does (R2370).
   * The game page passes the GO TO of the INBOX: the camera goes to the
   * cell. A line that stood before the call stays as it is.
   */
  setGo(fn: (cell: number) => void): void {
    this.go = fn;
  }

  /** A press on a line with a place: the act, then the line goes, because
   *  the player read it. */
  private press(ev: Event): void {
    if (!this.go || !isPrimaryPress(ev)) return;
    const line = (ev.target as Element | null)?.closest?.('.notice.go');
    if (!line || !this.el.contains(line)) return;
    const at = this.place.get(line);
    if (at === undefined) return;
    ev.preventDefault();
    this.drop(line);
    this.pump();
    this.go(at);
  }

  /** Makes one line a GO TO press, or takes the press away (R2370). */
  private arm(node: HTMLElement, at: number | undefined): void {
    const go = this.go !== null && at !== undefined && at > 0;
    node.classList.toggle('go', go);
    const tell = node.querySelector(':scope > .go-tell');
    if (go) {
      this.place.set(node, at);
      if (!tell) {
        // The line is no stop of the Tab walk, so a screen reader is not
        // told of a press it cannot reach: the INBOX GO TO is that path.
        const t = cell('span', 'go-tell', SN.go_tell);
        t.setAttribute('aria-hidden', 'true');
        node.append(t);
      }
    } else {
      this.place.delete(node);
      tell?.remove();
    }
  }

  /**
   * Sets the look. The game writes it one time, after the overlay is built,
   * because a theme cannot change during a match (themes.md §2); the theme
   * preview writes it on every show.
   */
  setStyle(id: NoticeStyle): void {
    if (id === this.styleId) return;
    this.styleId = id;
    this.clear();
    this.build();
  }

  /**
   * Where the two timer elements stand under this look, or null where they
   * ride the top bar. The owner of the elements moves them.
   */
  get timerCell(): HTMLElement | null { return this.cells?.tm ?? null; }

  /** Where a line goes: the lane's own cell, or the element itself. */
  private get host(): HTMLElement { return this.cells?.nt ?? this.el; }

  /** Gives the column the world-clock reading the `ledger` stamp writes. */
  setClock(read: () => string): void {
    this.clock = read;
  }

  /** Sets the repeat window. A value that is not positive is refused. */
  setDedup(ms: number): void {
    if (ms > 0) this.dedupMs = ms;
  }

  /** Sets the default life of a line. A value that is not positive is refused. */
  setMs(ms: number): void {
    if (ms > 0) this.ms = ms;
  }

  /**
   * Says whether a timer claims the lane, and redraws the count and the
   * `empty` class. The owner calls it beside the timer, every pass.
   */
  sync(timerOn: boolean): void {
    this.timerOn = timerOn;
    this.refresh();
  }

  /**
   * Puts one line in the column. `Overlay.notice` states the rules: the
   * duration is single-sourced here, and a repeat restarts the line on
   * screen instead of stacking a second copy under it.
   */
  push(text: string, opts: NoticeOptions | boolean = false): void {
    const o = typeof opts === 'boolean' ? { error: opts } : opts;
    const ms = this.lifeOf(o, text);
    const key = o.key ?? '';
    // Date.now, the clock every countdown of the chrome reads. The window
    // is two seconds of a person's time and needs no monotonic reading.
    const now = Date.now();
    const prev = this.last;
    if (prev && prev.node.isConnected && prev.key === key && prev.text === text
      && now - prev.at < this.dedupMs) {
      clearTimeout(prev.timer);
      // Restarting a running animation needs the browser to see it stop:
      // clear it, force a reflow, then let it run again from the top. The
      // hairline of `under-clock` is a second animation on a child, and it
      // drains over the same life, so it restarts with the line.
      const life = prev.node.querySelector<HTMLElement>('.life');
      prev.node.style.animation = 'none';
      if (life) life.style.animation = 'none';
      void prev.node.offsetHeight;
      prev.node.style.animation = '';
      if (life) life.style.animation = '';
      prev.node.style.setProperty('--life', `${ms}ms`);
      prev.at = now;
      prev.timer = this.expire(prev.node, ms);
      // The repeat takes the place of the newer emission (R2370).
      this.arm(prev.node, o.cell);
      this.said.set(prev.node, { text, opts: o });
      return;
    }
    // A repeat of the newest waiting line takes its place in the queue
    // and adds no second copy (R3081).
    const tail = this.queue[this.queue.length - 1];
    if (tail && tail.key === key && tail.text === text) {
      tail.opts = o;
      return;
    }
    // A line past the cap waits (R3081): the column shows it when a line
    // goes, or when an older line that is not kept has stood its least
    // life. The new line is never the one dropped (R2059).
    this.queue.push({ text, opts: o, key });
    if (this.queue.length > CHROME_NOTICE_QUEUE_MAX) {
      // The oldest waiting line that is not kept goes, and never the new
      // line; when every older line is kept, the oldest of them goes.
      const i = this.queue.findIndex((q, at) => !q.opts.keep && at < this.queue.length - 1);
      this.queue.splice(i < 0 ? 0 : i, 1);
    }
    this.pump();
  }

  /** The life of a line: the caller's figure, else the look's, else the
   *  event table's, and at least `CHROME_NOTICE_CHAR_MS` for each
   *  character, so a long line stands long enough to read (R3103). */
  private lifeOf(o: NoticeOptions, text: string): number {
    const base = o.ms && o.ms > 0 ? o.ms : (NOTICE_RENDERERS[this.styleId].life ?? this.ms);
    return Math.max(base, text.length * CHROME_NOTICE_CHAR_MS);
  }

  /**
   * Moves the queue onto the screen (R3081). A line goes up at once while
   * the column has room. In a full column the oldest line that is not
   * kept gives way once it has stood `CHROME_NOTICE_MIN_MS`; a kept line
   * gives way to no line and stands its whole life (R2059). The step
   * that waits for a least life sets one timer.
   */
  private pump(): void {
    if (this.pumpTimer) clearTimeout(this.pumpTimer);
    this.pumpTimer = 0;
    const r = NOTICE_RENDERERS[this.styleId];
    const host = this.host;
    while (this.queue.length > 0) {
      if (host.childElementCount < r.lines) {
        this.show(this.queue.shift()!);
        continue;
      }
      const old = host.querySelector(':scope > :not(.kept)');
      if (!old) break;
      const wait = (this.shownAt.get(old) ?? 0) + CHROME_NOTICE_MIN_MS - Date.now();
      if (wait > 0) {
        this.pumpTimer = setTimeout(() => this.pump(), wait);
        break;
      }
      this.drop(old);
    }
    this.refresh();
  }

  /** Puts one waiting line on the screen and starts its life. */
  private show(q: { text: string; opts: NoticeOptions; key: string }): void {
    const { text, opts: o, key } = q;
    const r = NOTICE_RENDERERS[this.styleId];
    const ms = this.lifeOf(o, text);
    const now = Date.now();
    const n = document.createElement('div');
    n.className = `panel notice${o.error ? ' error' : ''}${o.keep ? ' kept' : ''}${weightClass(key)}`;
    r.toast(n, text, o, this.clock());
    this.arm(n, o.cell);
    // The life is a PROPERTY and not an inline `animation-duration`: every
    // notices look names an animation of its own, and an inline duration
    // would stand over each one of them. The stylesheet states no length,
    // so the fade and the removal timer below still take one number.
    n.style.setProperty('--life', `${ms}ms`);
    this.said.set(n, { text, opts: o });
    this.host.appendChild(n);
    this.shownAt.set(n, now);
    this.last = { node: n, key, text, at: now, timer: this.expire(n, ms) };
  }

  /** Takes one line away before its life ends: its timer stops, and the
   *  repeat test forgets it. */
  private drop(line: Element): void {
    clearTimeout(this.timers.get(line));
    if (this.last?.node === line) this.last = null;
    line.remove();
  }

  /** Starts the life of one line: after `ms` the line goes. */
  private expire(node: HTMLElement, ms: number): ReturnType<typeof setTimeout> {
    const t = setTimeout(() => {
      if (this.last?.node === node) this.last = null;
      node.remove();
      this.pump();
    }, ms);
    this.timers.set(node, t);
    return t;
  }

  /**
   * Takes away every line that stands and is neither a refusal nor kept,
   * and answers what each one said, oldest first. The welcome card holds
   * the first frame, so a toast that rose before it waits for its close
   * (R2138).
   */
  liftStanding(): { text: string; opts: NoticeOptions }[] {
    const out: { text: string; opts: NoticeOptions }[] = [];
    for (const n of [...this.host.children]) {
      if (n.classList.contains('error') || n.classList.contains('kept')) continue;
      const said = this.said.get(n);
      if (!said) continue;
      this.drop(n);
      out.push(said);
    }
    // A waiting line rose before the card as well, so it waits too.
    const keep = this.queue.filter((q) => q.opts.error || q.opts.keep);
    for (const q of this.queue) if (!q.opts.error && !q.opts.keep) out.push({ text: q.text, opts: q.opts });
    this.queue = keep;
    if (out.length > 0) this.pump();
    return out;
  }

  /** Takes every line away and stops every timer. For a teardown. */
  clear(): void {
    if (this.last) clearTimeout(this.last.timer);
    this.last = null;
    this.queue = [];
    if (this.pumpTimer) clearTimeout(this.pumpTimer);
    this.pumpTimer = 0;
    for (const n of this.host.children) clearTimeout(this.timers.get(n));
    this.host.replaceChildren();
    this.refresh();
  }

  /** Builds the cells this look needs. The element holds nothing else. */
  private build(): void {
    this.el.replaceChildren();
    this.cells = null;
    this.lastMore = '';
    this.lastEmpty = null;
    if (NOTICE_RENDERERS[this.styleId].timerHost === 'column') {
      const tm = cell('span', 'tm');
      const div = cell('span', 'div');
      const nt = cell('span', 'nt');
      const more = cell('span', 'more');
      this.el.append(tm, div, nt, more);
      this.cells = { tm, div, nt, more };
    }
    this.refresh();
  }

  /**
   * Redraws the count and the `empty` class.
   *
   * A lane that holds neither a timer nor a line is `empty`, and the
   * stylesheet takes it off the screen. The count is how many lines wait
   * in the queue behind the one that stands (R3081): each one of them
   * comes on screen in its turn.
   */
  private refresh(): void {
    const lines = this.host.childElementCount;
    const c = this.cells;
    if (c) {
      const older = this.queue.length;
      const more = lines > 0 && older > 0
        ? (this.styleId === 'ribbon' ? SN.ribbon_more({ n: older }) : SN.lane_more({ n: older }))
        : '';
      if (more !== this.lastMore) {
        this.lastMore = more;
        c.more.textContent = more;
      }
      c.more.classList.toggle('hide', more === '');
      c.tm.classList.toggle('hide', !this.timerOn);
      c.div.classList.toggle('hide', !this.timerOn || lines === 0);
    }
    const empty = lines === 0 && !(c !== null && this.timerOn);
    if (empty !== this.lastEmpty) {
      this.lastEmpty = empty;
      this.el.classList.toggle('empty', empty);
    }
  }
}

/**
 * Writes one drain's `--frac` (R430) and answers with what it wrote.
 *
 * The property is the share of the schedule that is still to run, clamped
 * to [0, 1] and rounded to three decimals — a 2 px line shows no more than
 * that, and the rounding is what makes a change guard worth having on a
 * value that moves every frame. A total the client does not have yet reads
 * as a full line.
 */
export function writeDrainFrac(host: HTMLElement, was: number, frac: number): number {
  const v = Math.round(clamp01(isFinite(frac) ? frac : 1) * 1000) / 1000;
  if (v !== was) host.style.setProperty('--frac', String(v));
  return v;
}
